import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import { useEntityAlertMap } from '../../hooks/useEntityAlertMap';
import { ContractDetailsModal, JobStatusUpdateModal, JobReportingPlanModal } from '../../components';
import JobAssignmentModal from '../JobAssignmentsPage/JobAssignmentModal';
import { useJobs, useFirms, useCompanies, useContracts, useUsers, useJobTypes, SCREENS } from '../../hooks';
import { setGlobalSelectedContractWorkStatus } from '../../hooks/useJobs';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { hasuraClient } from '../../utils/graphqlClient';
import {
  UPDATE_JOB_NO_FINANCIAL_NEEDED,
  UPDATE_JOB_FORWARD_TO_ARGENET,
} from '../../graphql/mutations';
import { ROW_ACTIONS } from '../../constants/tableConfigurations';
import { useNotificationContext } from '../../providers/NotificationProvider';
import { formatDateTR } from '../../utils/dateUtils';

const JobManagementPage = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotificationContext();
  const { getRowSx } = useEntityAlertMap('job');
  const jobsData = useJobs({ paginated: true });
  // İş durumu filtresinin options'ı, mevcut jobs kayıtlarındaki distinct
  // job_status değerlerinden gelir — sabit enum listesi göstermek yerine
  // gerçek sistem durumunu yansıtır.
  const [distinctJobStatuses, setDistinctJobStatuses] = useState([]);
  // Teknik/mali sorumlu dropdown'larinda TUM kullanicilar yerine SADECE daha
  // once o pozisyonda atanmis kullanicilar gorunmeli. Sistemde
  // "is_technical/is_financial" tarzi bir bayrak yok; en pragmatik yol
  // job_assignments'tan distinct user id'leri cekmek. Yeni ilk atama sonrasi
  // liste kendini gunceller (refresh).
  const [technicalUserIds, setTechnicalUserIds] = useState([]);
  const [financialUserIds, setFinancialUserIds] = useState([]);
  useEffect(() => {
    let cancelled = false;
    hasuraClient.query(
      `query JobStatusOptions { jobs(distinct_on: job_status) { job_status } }`
    )
      .then((res) => {
        if (cancelled) return;
        const list = (res?.jobs || [])
          .map((j) => j.job_status)
          .filter(Boolean);
        setDistinctJobStatuses(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Teknik/mali distinct user id'leri.
  useEffect(() => {
    let cancelled = false;
    hasuraClient.query(
      `query DistinctAssignedUsers {
        tech: job_assignments(distinct_on: technical_user_id, where: { technical_user_id: { _is_null: false } }) { technical_user_id }
        fin:  job_assignments(distinct_on: financial_user_id, where: { financial_user_id: { _is_null: false } }) { financial_user_id }
      }`
    )
      .then((res) => {
        if (cancelled) return;
        setTechnicalUserIds((res?.tech || []).map(r => r.technical_user_id).filter(Boolean));
        setFinancialUserIds((res?.fin  || []).map(r => r.financial_user_id).filter(Boolean));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [reportingPlanModalOpen, setReportingPlanModalOpen] = useState(false);
  const [selectedJobForReporting, setSelectedJobForReporting] = useState(null);
  const [assignmentViewModalOpen, setAssignmentViewModalOpen] = useState(false);
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState(null);

  // Status modal açıkken job verisi güncellenince selectedJob'ı da güncelle
  useEffect(() => {
    if (!statusUpdateModalOpen || !selectedJob?.id || !Array.isArray(jobsData.jobs)) return;
    const updated = jobsData.jobs.find(j => j.id === selectedJob.id);
    if (updated) setSelectedJob(updated);
  }, [statusUpdateModalOpen, selectedJob?.id, jobsData.jobs]);

    const customFormFields = useCallback((fields, additionalData, isRootUser) => {
    const { companies, contracts, firms, jobTypes } = additionalData;
    const companiesList = companies?.companies || [];
    // İş formunda tüm şirket firmaları ve sözleşmeleri gösterilmeli (erişim kısıtı yok)
    const firmsList = firms?.referenceFirms || firms?.firms || [];
    const contractsList = contracts?.referenceContracts || contracts?.contracts || [];
    // Yalnizca aktif iş türleri form dropdown'inda görünmeli.
    const jobTypesList = (jobTypes?.jobTypes || []).filter(jt => jt.status === 'active');

    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companiesList.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'jobTypeId') {
          return {
            ...field,
            options: jobTypesList.map(jt => ({ value: jt.id, label: jt.name })),
          };
        }
        if (field.name === 'firmId') {
          return {
            ...field,
            options: firmsList.map(f => ({ value: f.id, label: f.firm_name })),
          };
        }
        if (field.name === 'firm_short_name') {
          return {
            ...field,
            type: 'select',
            options: firmsList.map(f => ({
              value: f.id,
              label: f.firm_short_name,
            })),
          };
        }
        if (field.name === 'contractId') {
          return {
            ...field,
            options: (formData) => {
              const selectedFirmId = formData.firm_established === 'yes'
                ? formData.firmId
                : formData.firm_short_name;

              if (!selectedFirmId) {
                return [];
              }

              const firmContracts = contractsList.filter(
                c => String(c.firm_id) === String(selectedFirmId)
              );

              return firmContracts.map(contract => {
                // Dropdown label: bir firmanin birden fazla sözlesmesi
                // olabildigi icin (ör. JFORCE) tarih + sözlesme aciklamasi
                // + mevcut is basliklarini ipucu olarak ekliyoruz.
                // Format: "Subject - dd.mm.yyyy - firmShortName · <desc> (job1, job2)"
                const signingDate = contract.contract_signing_date
                  ? formatDateTR(contract.contract_signing_date)
                  : '';
                const subject = contract.contract_subject?.name
                  || contract.scope_of_work?.name
                  || 'N/A';
                const firmLabel = contract.firm?.firm_short_name
                  || contract.firm?.firm_name
                  || contract.firm_short_name
                  || '';
                const desc = (contract.contract_description || '').trim();
                // Uzun aciklama yaza yaza dropdown item'i devirmesin.
                const descHint = desc
                  ? ` · ${desc.length > 60 ? `${desc.slice(0, 60)}…` : desc}`
                  : '';
                const existingTitles = Array.isArray(contract.jobs)
                  ? contract.jobs
                    .map(j => (j.job_title || '').trim())
                    .filter(Boolean)
                  : [];
                const jobsHint = existingTitles.length > 0
                  ? ` (${existingTitles.slice(0, 2).join(', ')}${existingTitles.length > 2 ? '…' : ''})`
                  : '';
                const parts = [subject, signingDate || '—'];
                if (firmLabel) parts.push(firmLabel);
                return {
                  value: contract.id,
                  label: `${parts.join(' - ')}${descHint}${jobsHint}`,
                  contract: contract,
                };
              });
            },
            onChange: (name, value, event, formData, onChange) => {
              if (value) {
                const selectedContract = contractsList.find(c => c.id === value);

                if (selectedContract?.work_status?.name) {
                  setGlobalSelectedContractWorkStatus(selectedContract.work_status.name);
                }
              } else {
                setGlobalSelectedContractWorkStatus(null);
              }
            }
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  const customFilterConfigs = useCallback((filters, additionalData, isRootUser) => {
    const usersList = additionalData?.users?.users || [];
    const jobTypesList = (additionalData?.jobTypes?.jobTypes || []).filter(jt => jt.status === 'active');
    return filters
      .filter(f => (f.showIfRoot ? isRootUser : true))
      .map(f => {
        // Teknik/Mali sorumlu filtre dropdown'ları için kullanıcı seçeneklerini
        // additionalData.users'tan dolduruyoruz (tags pattern'i). Filter value
        // user_id (number) — server-side hasuraField ile _eq, client-side de
        // row.technical_user_id / row.financial_user_id ile eşleşir.
        if (f.field === 'technical_user_id' || f.field === 'financial_user_id') {
          // Sadece daha once o pozisyonda atanmis kullanicilari dropdown'a
          // koy — "TUM personel" listesi gorunmesin (kullanicinin bug
          // raporunda belirttigi durum).
          const allowedIds = f.field === 'technical_user_id' ? technicalUserIds : financialUserIds;
          const allowedSet = new Set(allowedIds);
          return {
            ...f,
            // useUsers transformData camelCase'e map ediyor (firstName/lastName);
            // snake_case fallback'i de korunsun (başka kaynaktan gelirse).
            options: usersList
              .filter(u => allowedSet.has(u.id))
              .map(u => {
                const first = u.firstName || u.first_name || '';
                const last = u.lastName || u.last_name || '';
                const fullName = `${first} ${last}`.trim();
                return {
                  value: u.id,
                  label: fullName || `Kullanıcı #${u.id}`,
                };
              }),
          };
        }
        if (f.field === 'job_type') {
          // Dinamik iş türleri filtresi: server tarafinda job_type_id _eq ile
          // sorgulayabilmek icin hasuraField'i de set ediyoruz.
          return {
            ...f,
            hasuraField: 'job_type_id',
            options: jobTypesList.map(jt => ({ value: jt.id, label: jt.name })),
          };
        }
        if (f.field === 'job_status') {
          return {
            ...f,
            options: distinctJobStatuses.map((s) => {
              const key = `jobs.jobStatuses.${s}`;
              const translated = t(key);
              return {
                value: s,
                label: translated === key ? s : translated,
              };
            }),
          };
        }
        if (f.field === 'contract_received') {
          // Config'te labelKey duruyor (tableConfigurations); TableFilters
          // label'ı ham bastığı için çeviriyi burada yapıyoruz.
          return {
            ...f,
            options: (f.options || []).map(o => ({
              value: o.value,
              label: o.labelKey ? t(o.labelKey) : o.label,
            })),
          };
        }
        return f;
      });
  }, [t, distinctJobStatuses, technicalUserIds, financialUserIds]);

  const transformEditData = useCallback(jobData => {
    if (!jobData) return {};
    return {
      ...jobData,
      companyId: jobData.company_id,
      firm_established: jobData.firm_established ? 'yes' : 'no',
      firmId: jobData.firm_id,
      firm_short_name: jobData.firm_id,
      jobStatus: jobData.contract?.work_status?.name || jobData.jobStatus,
      // Düzenleme modunda yeni iş türü dropdown'u secili gelmeli; FK
      // (job_type_id) yoksa legacy enum metnine fallback yapilir.
      jobTypeId: jobData.job_type_id ?? jobData.jobTypeId ?? null,
      jobType: jobData.job_type?.name || jobData.jobType || jobData.job_type_enum || null,
    };
  }, []);

  const handleViewContract = useCallback((row) => {
    if (row.contract) {
      setSelectedContract(row.contract);
      setContractModalOpen(true);
    }
  }, []);

  const handleCloseContractModal = useCallback(() => {
    setContractModalOpen(false);
    setSelectedContract(null);
  }, []);

  const handleUpdateStatus = useCallback((row) => {
    setSelectedJob(row);
    setStatusUpdateModalOpen(true);
  }, []);

  const handleCloseStatusUpdateModal = useCallback(() => {
    setStatusUpdateModalOpen(false);
    setSelectedJob(null);
  }, []);

  const handleUpdateReportingPlan = useCallback((row) => {
    setSelectedJobForReporting(row);
    setReportingPlanModalOpen(true);
  }, []);

  const handleCloseReportingPlanModal = useCallback(() => {
    setReportingPlanModalOpen(false);
    setSelectedJobForReporting(null);
  }, []);

  const handleViewJobAssignment = useCallback((row) => {
    if (!row) return;

    const assignment = row.job_assignment || null;

    setSelectedJobForAssignment({
      id: row.id,
      job_id: row.id,
      job_title: row.job_title || row.jobTitle,
      job_status: row.job_status || row.jobStatus,
      assignment_id: assignment?.id || null,
      assignmentData: assignment || null,
    });
    setAssignmentViewModalOpen(true);
  }, []);

  const handleCloseAssignmentViewModal = useCallback(() => {
    setAssignmentViewModalOpen(false);
    setSelectedJobForAssignment(null);
  }, []);

  const handleStatusUpdateSuccess = useCallback((successMessage) => {
    showNotification(successMessage || t('jobStatusUpdate.successMessage'), 'success');
  }, [showNotification, t]);

  const handleStatusUpdateError = useCallback((errorMessage) => {
    showNotification(errorMessage, 'error');
  }, [showNotification]);

  const handleReportingPlanSuccess = useCallback((successMessage) => {
    showNotification(successMessage || t('jobReportingPlan.successMessage'), 'success');
    // Raporlama planı güncellendikten sonra iş listesini yenile
    jobsData.triggerRefresh?.();
  }, [showNotification, t, jobsData.triggerRefresh]);

  const handleReportingPlanError = useCallback((errorMessage) => {
    showNotification(errorMessage, 'error');
  }, [showNotification]);

  const customRowActions = useCallback(
    row => {
      const baseActions = ROW_ACTIONS.jobs(row);
      // ArgeNet'e ilet — isaretlenen is, ArgeNet'te AR-GE birimi olarak
      // acilir (sync cron ceker). Isareti kaldirmak oradaki birimi SILMEZ,
      // yalnizca guncellenmesini durdurur.
      const argenetAction = row?.forward_to_argenet
        ? {
            type: 'argenetOff',
            label: 'jobs.actions.argenetOff',
            icon: <CloudOffIcon fontSize="small" />,
            color: 'default',
          }
        : {
            type: 'argenetOn',
            label: 'jobs.actions.argenetOn',
            icon: <CloudSyncIcon fontSize="small" />,
            color: 'info',
          };
      // "Mali sorumlu istenmiyor" işaretli işler atama listesinden düşer →
      // tekrar açma aksiyonu burada (İşler sayfasında). Sadece işaretliyse görünür.
      if (row?.no_financial_needed) {
        return [
          ...baseActions,
          argenetAction,
          {
            type: 'noFinancialOn',
            label: 'jobAssignments.actions.noFinancialOn',
            icon: <RestoreIcon fontSize="small" />,
            color: 'info',
          },
        ];
      }
      return [...baseActions, argenetAction];
    },
    []
  );

  const handleToggleForwardToArgenet = useCallback(async (row, next) => {
    const jobId = row?.id;
    if (!jobId) return;
    try {
      await hasuraClient.mutate(UPDATE_JOB_FORWARD_TO_ARGENET, {
        id: jobId,
        forward_to_argenet: next,
      });
      showNotification(
        t(next ? 'jobs.toast.argenetOn' : 'jobs.toast.argenetOff'),
        'success',
      );
      jobsData.triggerRefresh?.();
    } catch (err) {
      showNotification(err?.message || t('jobs.toast.argenetError'), 'error');
    }
  }, [showNotification, t, jobsData.triggerRefresh]);

  const handleReopenFinancialNeeded = useCallback(async (row) => {
    const jobId = row?.id;
    if (!jobId) return;
    try {
      await hasuraClient.mutate(UPDATE_JOB_NO_FINANCIAL_NEEDED, {
        id: jobId,
        value: false,
      });
      jobsData.triggerRefresh?.();
      showNotification(
        t('jobAssignments.actions.noFinancialOnSuccess', 'Mali sorumlu talebi tekrar aktif.'),
        'success'
      );
    } catch (err) {
      showNotification(
        t('jobAssignments.actions.noFinancialError', 'İşlem sırasında hata oluştu.'),
        'error'
      );
    }
  }, [jobsData, showNotification, t]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'viewContract') {
      handleViewContract(row);
      return true;
    }
    if (actionType === 'updateStatus') {
      handleUpdateStatus(row);
      return true;
    }
    if (actionType === 'updateReportingPlan') {
      handleUpdateReportingPlan(row);
      return true;
    }
    if (actionType === 'viewJobAssignment') {
      handleViewJobAssignment(row);
      return true;
    }
    if (actionType === 'noFinancialOn') {
      handleReopenFinancialNeeded(row);
      return true;
    }
    if (actionType === 'argenetOn' || actionType === 'argenetOff') {
      handleToggleForwardToArgenet(row, actionType === 'argenetOn');
      return true;
    }
    return false;
  }, [handleViewContract, handleUpdateStatus, handleUpdateReportingPlan, handleViewJobAssignment, handleReopenFinancialNeeded, handleToggleForwardToArgenet]);

  return (
    <>
      <DynamicCrudPage
        entityType="jobs"
        screen={SCREENS.JOB_MANAGEMENT}
        hook={() => jobsData}
        additionalHooks={{
          firms: useFirms,
          companies: useCompanies,
          contracts: useContracts,
          users: useUsers,
          jobTypes: useJobTypes,
        }}
        addButtonTextKey="table.actions.addJob"
        addButtonIcon={<AddIcon />}
        entityTypeForDataTable="job"
        customFormFields={customFormFields}
        customFilterConfigs={customFilterConfigs}
        customTransformEditData={transformEditData}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        showAddButton={true}
        getRowSx={getRowSx}
      />
      
      <ContractDetailsModal
        open={contractModalOpen}
        onClose={handleCloseContractModal}
        contract={selectedContract}
      />
      
      <JobStatusUpdateModal
        open={statusUpdateModalOpen}
        onClose={handleCloseStatusUpdateModal}
        job={selectedJob}
        onSuccess={handleStatusUpdateSuccess}
        onError={handleStatusUpdateError}
        onRefresh={jobsData.triggerRefresh}
      />
        
      <JobReportingPlanModal
        open={reportingPlanModalOpen}
        onClose={handleCloseReportingPlanModal}
        job={selectedJobForReporting}
        onSuccess={handleReportingPlanSuccess}
        onError={handleReportingPlanError}
      />

      <JobAssignmentModal
        open={assignmentViewModalOpen}
        onClose={handleCloseAssignmentViewModal}
        selectedJob={selectedJobForAssignment}
        onSuccess={jobsData.triggerRefresh}
      />
    </>
  );
};

export default JobManagementPage;
